import { CellContext, createColumnHelper } from '@tanstack/react-table';

import { columnDefExtended, DataTableObject, TableConfigType } from '@/components/PatientDataTable';
import { DETAILS_SIDE_PANEL } from '@/components/PatientDataTable/consts';
import { PatientObject } from '@/types';
import { formatDate } from '@/utils';
import { formatScreeningStatus } from '@/utils/statusUtils';

import { LastContactedCell } from '../components/LastContactedCell';
import { FetchPatientScreeningObject } from '../types';

type CellContextExtended = CellContext<DataTableObject, string> & FetchPatientScreeningObject;
const columnHelper = createColumnHelper<DataTableObject>();

const columns = [
	columnHelper.accessor('identifier', {
		header: () => 'uMed Study ID',
		cell: (info) => info.getValue(),
		enableSorting: true,
	}),
	columnHelper.accessor(
		(row) =>
			`${(row as FetchPatientScreeningObject).patient_first_name} ${
				(row as FetchPatientScreeningObject).patient_last_name
			}`,
		{
			header: () => 'Patient Name',
			id: 'name',
			enableSorting: true,
			isPii: true,
		} as columnDefExtended
	),
	columnHelper.display({
		id: 'status',
		header: () => 'Status',
		cell: (props) => {
			const { Element } = formatScreeningStatus(
				props.row.original as PatientObject,
				'screening'
			);
			return Element;
		},
		enableSorting: true,
	}),
	columnHelper.display({
		id: 'last_contact_at',
		header: () => 'Last contact',
		cell: (props) => <LastContactedCell props={props as CellContextExtended} />,
		enableSorting: true,
	}),
	columnHelper.accessor('contact_booked_for', {
		header: () => 'Call booked',
		cell: (info) => formatDate(info.getValue() as string) || null,
		enableSorting: true,
	}),
	columnHelper.accessor('provider_name', {
		header: () => 'Provider',
		cell: (info) => info.getValue() || null,
		enableSorting: true,
	}),
	columnHelper.accessor('contact_email', {
		id: 'contact_email',
		header: () => 'Email',
		cell: (info) => info.getValue() || null,
		enableSorting: true,
		isPii: true,
	} as columnDefExtended),
	columnHelper.accessor('date_of_birth', {
		id: 'date_of_birth',
		header: () => 'Date of birth',
		cell: (info) => formatDate(info.getValue() as string),
		enableSorting: true,
		isPii: true,
	} as columnDefExtended),
	columnHelper.accessor('contact_mobile_number', {
		id: 'contact_mobile_number',
		header: () => 'Mobile number',
		cell: (info) => info.getValue() || null,
		enableSorting: true,
		isPii: true,
	} as columnDefExtended),
];

const rowProps = {
	batchSelect: false,
	onRowClickTarget: DETAILS_SIDE_PANEL,
	rowSelect: true,
	getRowId: (row: DataTableObject) => (row as FetchPatientScreeningObject).id,
};

const columnVisibility = {
	name: false,
	date_of_birth: false,
	contact_email: false,
	contact_mobile_number: false,
};

export const patientScreeningTableConfig: TableConfigType = {
	columns: columns,
	rowProps,
	defaultTabIndex: 1,
	visibleColumnDef: columnVisibility,
};
